import React, { useState } from 'react';
import { ExpenseResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import ExpenseSimulation from '../simulations/ExpenseSimulation';
import { modelStories } from '../../data/stories';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';

interface ExpensesModuleProps {
  data: ExpenseResult | null;
  onOptimizationComplete: (data: ExpenseResult) => void;
  onLoadingStart: () => void;
}

const ExpensesModule: React.FC<ExpensesModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleDetect = async () => {
    onLoadingStart();
    const result = await api.detectAnomalies();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  const getRiskBadge = (impact: string) => {
    const colors = {
      High: 'bg-red-100 text-red-800 border-red-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[impact as keyof typeof colors] || colors.Low;
  };

  const getRiskIcon = (impact: string) => {
    if (impact === 'High') return <AlertTriangle className="w-4 h-4" />;
    if (impact === 'Medium') return <AlertCircle className="w-4 h-4" />;
    return <Info className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Expense Anomaly Detection</h2>
        <p className="text-gray-600 mb-4">
          Detect fraud and policy violations using ensemble machine learning
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.expenses} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleDetect} className="btn-primary">Detect Anomalies</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Total Detected</div>
              <div className="text-2xl font-bold text-red-600">{data.detected}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">High Risk</div>
              <div className="text-2xl font-bold text-red-600">{data.high_risk}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Medium Risk</div>
              <div className="text-2xl font-bold text-yellow-600">{data.medium_risk}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Total Amount</div>
              <div className="text-2xl font-bold text-gray-800">{formatCurrency(data.total)}</div>
            </div>
          </div>

          {/* Anomalies Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Detected Anomalies (Top 5)</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Impact</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.anomalies.map((anomaly) => (
                  <tr key={anomaly.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{anomaly.id}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(anomaly.amount)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{anomaly.category}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        {(anomaly.score * 100).toFixed(0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{anomaly.reason}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getRiskBadge(anomaly.impact)}`}>
                        {getRiskIcon(anomaly.impact)}
                        <span>{anomaly.impact}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Recommended Actions</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <ExpenseSimulation />
      )}
    </div>
  );
};

export default ExpensesModule;
